import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Building2, ArrowRight, Eye, EyeOff, AlertCircle, CheckCircle2, Sparkles } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_825c5321-5d95-4d75-842e-1e4ffe27dc80/artifacts/nz2oqlak_image.png';

const benefits = [
  'Full platform access for 30 days',
  'No credit card required',
  'Unlimited RFQs and BOMs',
  'AI-powered costing features',
  'Priority onboarding support',
];

export const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the terms and conditions');
      return;
    }

    setIsLoading(true);

    // Mock signup - simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Mock validation
    if (formData.email && formData.password && formData.name) {
      // Store mock auth state with trial info
      localStorage.setItem('costgenie_user', JSON.stringify({
        email: formData.email,
        name: formData.name,
        company: formData.company,
        plan: 'Trial',
        trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        createdAt: new Date().toISOString(),
      }));
      navigate('/dashboard');
    } else {
      setError('Please fill in all required fields');
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="signup-page">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#030810] via-[#051020] to-[#030810]" />
      <div className="absolute top-[-20%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-cyan-500/5 blur-[100px]" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-purple-500/5 blur-[80px]" />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            className="hidden lg:block"
          >
            <Link to="/" className="inline-block mb-8">
              <img src={LOGO_URL} alt="CostGenie" className="h-10 w-auto" />
            </Link>

            <h2 className="text-3xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Start Your <span className="text-gradient-cyan">30-Day</span> Free Trial
            </h2>
            <p className="text-white/50 mb-8">
              Experience the full power of CostGenie with no limitations. 
              Transform your RFQ-to-quote process in days, not months.
            </p>

            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-3 h-3 text-cyan-400" />
                  </div>
                  <span className="text-white/70 text-sm">{benefit}</span>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-transparent border border-cyan-500/20">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                <div>
                  <p className="text-white/80 text-sm font-medium">Go-Live in 30-60 Days</p>
                  <p className="text-white/40 text-xs">With our guided onboarding program</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-8">
              <Link to="/">
                <img src={LOGO_URL} alt="CostGenie" className="h-10 w-auto mx-auto mb-4" />
              </Link>
              <h2 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                Start Your Free Trial
              </h2>
            </div>

            <div className="auth-card" data-testid="signup-card">
              <div className="text-center mb-6 lg:text-left">
                <h1 className="text-xl font-semibold mb-1 hidden lg:block" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Create your account
                </h1>
                <p className="text-white/40 text-sm">No credit card required</p>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 mb-6 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
                  data-testid="signup-error"
                >
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="auth-input pl-11"
                        placeholder="John Doe"
                        required
                        data-testid="signup-name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/60 text-sm mb-2">Company</label>
                    <div className="relative">
                      <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="auth-input pl-11"
                        placeholder="Company Inc."
                        data-testid="signup-company"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Work Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="auth-input pl-11"
                      placeholder="you@company.com"
                      required
                      data-testid="signup-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white/60 text-sm mb-2">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="auth-input pl-11 pr-11"
                      placeholder="Min. 8 characters"
                      minLength={8}
                      required
                      data-testid="signup-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 text-cyan-500 focus:ring-cyan-500/30"
                    data-testid="signup-terms"
                  />
                  <label htmlFor="terms" className="text-white/50 text-xs leading-relaxed">
                    I agree to the{' '}
                    <Link to="/terms" className="text-cyan-400 hover:text-cyan-300">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300">Privacy Policy</Link>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full btn-premium btn-premium-primary justify-center mt-6"
                  data-testid="signup-submit"
                >
                  {isLoading ? (
                    <span className="relative z-10">Creating account...</span>
                  ) : (
                    <>
                      {/* <span className="relative z-10">Start Free Trial</span> */}
                      <span className="relative z-10">Sign Up</span>
                      <ArrowRight className="w-4 h-4 relative z-10" />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-white/40 text-sm">
                  Already have an account?{' '}
                  <Link to="/login" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
